import { useLazyQuery, useMutation } from '@apollo/client'
import { css, StyleSheet } from 'aphrodite'
import jwtDecode from 'jwt-decode'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import api from '../../api'
import jacket from '../../assets/jacket.jpg'
import LayoutCol from '../../components/Layout/LayoutCol'
import UIButton from '../../components/UI/UIButton'
import UITitle from '../../components/UI/UITitle'
import WidgetMakerHeader from '../../components/Widget/WidgetMakerHeader'
import PaymentForm from '../../components/Widget/WidgetPaymentForm/PaymentForm'
import createOrder from '../../graphql/Order/createOrder'
import createOrderArticle from '../../graphql/Order/createOrderArticle'
import fetchArticles from '../../graphql/Order/getArticles'
import GET_COEYE_ORDERS from '../../graphql/Order/getOrders'
import UPDATE_ORDER_ARTICLE from '../../graphql/Order/updateOrderArticle'
import { COLOR_GREEN__DARK, COLOR_TURQUOISE } from '../../lib/colors'
import { autoLogin, leToBr } from '../../utils/Utils'

const styles = StyleSheet.create({
  containerForm: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '100px',
  },
  title: {
    color: COLOR_GREEN__DARK,
    fontSize: '30px',
    '@media (max-width: 480px)': {
      whiteSpace: 'break-spaces',
    },
  },
  description: {
    margin: 0,
    lineHeight: '2em',
    '@media (max-width: 480px)': {
      lineHeight: '1.6em',
    },
  },
  imageContainer: {
    padding: '3em 0em',
    display: 'flex',
    justifyContent: 'center',
    '@media (max-width: 480px)': {
      padding: '0px',
    },
  },
  image: {
    width: '100%',
    '@media (max-width: 480px)': {
      width: '100%',
      paddingBottom: '20px',
      paddingTop: '10px',
    },
  },
  button: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  buttonLabel: {
    margin: 0,
    '@media (max-width: 480px)': {
      padding: '0',
    },
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: '65px',
    '@media (max-width: 480px)': {
      gap: '10px',
      flexDirection: 'column',
    },
  },
  paymentContainer: {
    marginTop: '50px',
    paddingBottom: '100px',
  },
  paymentTitle: {
    color: COLOR_GREEN__DARK,
    textAlign: 'center',
  },
  paymentSuccessLabel: {
    textAlign: 'center',
    color: COLOR_TURQUOISE,
  },
})

export default function BuyingVest() {
  const { t } = useTranslation('translation')
  const navigate = useNavigate()
  const [coeyesOrder, setCoeyesOrder] = useState([])
  const [idCoeye, setIdCoeye] = useState(undefined)

  const paymentRef = useRef(null)

  const [vestSize, setVestSize] = useState({
    sizeM: '',
    sizeXL: '',
  })
  const [stripeClientSecret, setStripeClientSecret] = useState('')
  const [paymentSucces, setPaymentSuccess] = useState(false)
  const [loading, setLoading] = useState(true)
  const [haveAlreadyPaid, setHaveAlreadyPaid] = useState(false)

  const [fetchAllArticles] = useLazyQuery(fetchArticles, {
    onCompleted: (articles) => {
      setLoading(false)
      setVestSize(() => {
        const arrayM = articles.articles.nodes.filter(
          (item) => item.label === 'Gilet M'
        )
        const arrayXL = articles.articles.nodes.filter(
          (item) => item.label === 'Gilet XL'
        )
        return { sizeM: arrayM[0].id, sizeXL: arrayXL[0].id }
      })
    },
    onError: () => {
      toast.error(t('error'))
    },
  })

  const [searchParams] = useSearchParams()
  const challenge = searchParams.get('challenge')
  const email = searchParams.get('email')

  useEffect(() => {
    if (!challenge && !email) return
    autoLogin(challenge, email)
      .then(() => {
        fetchAllArticles()
      })
      .catch(() => {
        toast.error(t('error'))
        navigate('/')
      })
  }, [challenge, email])

  useEffect(() => {
    if (stripeClientSecret) {
      window.scrollTo({ behavior: 'smooth', top: paymentRef.current.offsetTop })
    }
  }, [stripeClientSecret])

  useEffect(() => {
    if (!localStorage.getItem('access_token')) {
      toast.error(t('error'))
      return
    }
    const idCoeye = jwtDecode(localStorage.getItem('access_token')).sub
    setIdCoeye(idCoeye)
    if (idCoeye) {
      verifyCoeyeOrders(idCoeye)
    }
  }, [])

  //url ex : http://localhost:3000/buying-vest/?challenge=UTVF__jUOg0H9NSOKufHnEq2kQKIRVBf92VGSd56vfvyf5GOL9KoSnF73HlhS9xjKMeY8k2YZiVb7oax_Pk8Hes5toqmDSQd8Md5IcQcD2TD2jlgZY6cOWH37p2SJLGWSGQ0hhvlXLyRtYhRzSd4o7vg9jwRT5ftziG5CpdqTasJ9JnJssH2i6nwfX0UVo5B4h5IP3zZKVIxK4XqJgFKWLhsDIgdCV8WHaBg6hVkzlL-bsK_eOOmR6Te6MFPLmqCLijUFQhcU6bCviRb_8GFNcKCHJDJetPZE98I93n_89cx5Gk4v732s6swAJBOIwqOginGf_8WbcxIoQjkLlFAhg&email=david.puche%2Bcoeye%40bettercalldave.io

  const [addOrder] = useMutation(createOrder, {
    onError: () => {
      toast.error(t('pages.buyingVest.webServiceError'))
    },
  })

  const [addOrderArticle] = useMutation(createOrderArticle, {
    onError: () => {
      toast.error(t('pages.buyingVest.webServiceError'))
    },
    onCompleted: (data) => {
      const coeyeOrders = []
      coeyeOrders.push({
        id: data.createOrderArticle.orderByIdOrder.id,
        status: 'CREATED',
        idArticle: data.createOrderArticle.orderArticle.id,
      })
      setCoeyesOrder(coeyeOrders)
    },
  })

  const [updateOrderArticle] = useMutation(UPDATE_ORDER_ARTICLE, {
    onError: () => {
      toast.error(t('pages.buyingVest.webServiceError'))
    },
  })

  const [getCoeyeOrders] = useLazyQuery(GET_COEYE_ORDERS, {
    onCompleted: (data) => {
      const coeyeOrders = []
      if (data.coeye.ordersByIdCoeye.nodes.length > 0) {
        data.coeye.ordersByIdCoeye.nodes.forEach((order) => {
          coeyeOrders.push({
            id: order.id,
            status: order.status,
            idArticle: order.orderArticlesByIdOrder.nodes[0].id,
          })
        })

        setCoeyesOrder(coeyeOrders)
      } else {
        setCoeyesOrder([])
      }
    },
  })

  const verifyCoeyeOrders = async (idCoeye) => {
    await getCoeyeOrders({
      variables: {
        idCoeye,
      },
    })
  }
  const isOrderPaid = () => {
    return coeyesOrder.some(
      (order) =>
        order.status === 'PAID' ||
        order.status === 'SENT' ||
        order.status === 'DELIVERED'
    )
  }

  const createCommand = async (id) => {
    if (coeyesOrder.length === 0) {
      await toast.promise(
        addOrder({
          variables: {
            idCoeye,
          },
        }).then((data) => {
          api
            .fetchPaymentIntent(data.data.createOrder.order.id)
            .then((dataPayment) =>
              setStripeClientSecret(dataPayment.stripeClientSecret)
            )
          addOrderArticle({
            variables: {
              idOrder: data.data.createOrder.order.id,
              idArticle: id,
            },
          })
        }),
        {
          success: t('pages.buyingVest.successMessage'),
          pending: t('pages.buyingVest.pendingMessage'),
          error: t('pages.buyingVest.errorMessage'),
        }
      )
    } else {
      if (isOrderPaid()) {
        setHaveAlreadyPaid(true)
      } else {
        const orderToUpdate = coeyesOrder.find(
          (order) => order.status === 'CREATED'
        )
        await toast.promise(
          updateOrderArticle({
            variables: {
              idOrder: orderToUpdate.id,
              idArticle: id,
              id: orderToUpdate.idArticle,
            },
          }).then(() => {
            api
              .fetchPaymentIntent(orderToUpdate.id)
              .then((dataPayment) =>
                setStripeClientSecret(dataPayment.stripeClientSecret)
              )
          }),

          {
            success: t('pages.buyingVest.successMessage'),
            pending: t('pages.buyingVest.pendingMessage'),
            error: t('pages.buyingVest.errorMessage'),
          }
        )
      }
    }
  }

  return (
    <>
      <WidgetMakerHeader
        handleBecomeMakerClick={() => {}}
        handleGlobeClick={function noRefCheck() {}}
      />
      <div className={css(styles.containerForm)}>
        <LayoutCol col={10} xs={5}>
          <UITitle
            dynamicStyle={styles.title}
            label={t('pages.buyingVest.title')}
          />
          <p className={css(styles.description)}>
            {leToBr(t('pages.buyingVest.description'))}
          </p>
          <div className={css(styles.imageContainer)}>
            <img className={css(styles.image)} src={jacket} />
          </div>
          {!paymentSucces && !loading && (
            <div className={css(styles.buttonContainer)}>
              <UIButton
                design="primary"
                smaller={true}
                onClick={() => createCommand(vestSize.sizeM)}
                label={t('pages.buyingVest.sizeM')}
                withForwardChevron
              />
              <UIButton
                design="primary"
                smaller={true}
                onClick={() => createCommand(vestSize.sizeXL)}
                label={t('pages.buyingVest.sizeXL')}
                withForwardChevron
              />
            </div>
          )}

          {haveAlreadyPaid && (
            <p className={css(styles.paymentSuccessLabel)}>
              {t('pages.buyingVest.alreadyBuy')}
            </p>
          )}

          {paymentSucces && (
            <p className={css(styles.paymentSuccessLabel)}>
              {t('pages.payment.successPayment')}{' '}
            </p>
          )}
          {stripeClientSecret && !paymentSucces && (
            <div ref={paymentRef} className={css(styles.paymentContainer)}>
              <h2 className={css(styles.paymentTitle)}>
                {t('pages.payment.paymentInformation')}
              </h2>
              <PaymentForm
                clientSecret={stripeClientSecret}
                closeModalPayment={() => {
                  setStripeClientSecret('')
                  setPaymentSuccess(true)
                }}
              />
            </div>
          )}
        </LayoutCol>
      </div>
    </>
  )
}
